import { useState, type ReactNode } from 'react';
import { Alert, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { getDashboard, type PAAccount, type PADashboard } from '../api/money';
import { QueryFrame } from './MoneyPage';
import { usePAQuery } from './usePAQuery';

/**
 * ============================================================================
 * AccountScope — «which Account am I looking at», answered ONCE for every
 * Account-scoped section
 * ============================================================================
 *
 * Four sections of this portal are Account-scoped rather than contract-scoped — devices,
 * additional services, the tariff change and the statistics — because their routes take the
 * Account in the PATH (`/pa/services/{id}`, `/pa/tariff/{id}`, `/pa/statistics/{id}`). Each
 * therefore needs an account before it can ask anything, and §7a/§14/§17/§24 all reach the
 * detail the same way: «click the Account login».
 *
 * ⚠ THE ACCOUNT LIST COMES FROM THE DASHBOARD READ, and that is a choice worth stating.
 * `GET /pa/money/dashboard` is this SPA's one canonical per-contract account list, already
 * carrying the login, the status and the tariff name — so the picker here and the cards on the
 * landing page cannot come to disagree about which accounts exist. The alternative for each
 * section would be a second list read with its own idea of the set.
 *
 * ⚠ AND IT IS ONE COMPONENT RATHER THAN FOUR COPIES for the reason `DataTable` states about
 * the two markups it refuses to have: four copies of «resolve the chosen id against the
 * freshly loaded list» are four chances for one of them to leave the picker pointing at an
 * account the contract no longer serves, and each copy looks right on its own screen. (⚠ ONE
 * PRE-EXISTING COPY REMAINS: `DevicesScreen` was written before this component and keeps its
 * own, because rewriting a shipped screen is not this sub-wave's work — recorded here rather
 * than left to be discovered, and it is the first thing to fold in if a fifth section lands.)
 *
 * ⚠ NO PICKER FOR A SINGLE ACCOUNT. Most subscribers have exactly one, and a dropdown with
 * one option is the dead control this phase has already removed three times (the language
 * switch, the recovery-channel radio, the one-contract chooser).
 */
export function AccountScope({
  contractID,
  labelKey,
  emptyKey,
  selectID,
  children,
}: {
  contractID: number;
  /** i18n key of the picker's label — never a literal (ST13). */
  labelKey: string;
  /** i18n key of «this contract has no accounts», which is a fact and not a failure. */
  emptyKey: string;
  /** DOM id, so each screen's own `<label for>` pairs with its own control. */
  selectID: string;
  children: (account: PAAccount) => ReactNode;
}) {
  const { t } = useTranslation();
  const { query, reload } = usePAQuery<PADashboard>(() => getDashboard(contractID), [contractID]);
  const [chosen, setChosen] = useState<number | null>(null);

  return (
    <QueryFrame query={query} reload={reload}>
      {(dashboard) => {
        const rows = dashboard.accounts;
        if (rows.length === 0) {
          return <Alert type="info" showIcon message={t(emptyKey)} />;
        }
        // The explicit choice, or the first account. `chosen` is re-resolved against the
        // freshly loaded list on every render, so a reload never leaves the picker on an
        // account the contract has stopped serving.
        const account = rows.find((a) => a.account_id === chosen) ?? rows[0];
        return (
          <>
            {rows.length > 1 ? (
              <div className="pa-picker">
                <label className="pa-picker-label" htmlFor={selectID}>
                  {t(labelKey)}
                </label>
                <Select<number>
                  id={selectID}
                  className="pa-picker-select"
                  value={account.account_id}
                  onChange={setChosen}
                  options={rows.map((a) => ({ value: a.account_id, label: a.login }))}
                />
              </div>
            ) : null}
            {children(account)}
          </>
        );
      }}
    </QueryFrame>
  );
}
